import { inject, Injectable } from '@angular/core';
import type { GetSingleEventQuery } from '@uigy/generated/graphql';
import {
  ActionType,
  Capability,
  PolicyFindingFilterField,
  ExclusionType,
  ExprOp,
  PolicyFindingFragment,
  PolicyFindingsExpressionsParams,
} from '@uigy/generated/graphql';
import { ExcludeFromPolicyComponent } from '@uigy/modules/events/components/exclude-from-policy/exclude-from-policy.component';
import {
  EventOrFindingEvent,
  FortigatePolicyFormComponent,
  ReActOnEventResponse,
} from '@uigy/modules/events/components/fortigate-policy-form/fortigate-policy-form.component';
import { ResolveFormComponent } from '@uigy/modules/events/components/resolve-form/resolve-form.component';
import { ExclusionSliderInput, ExclusionSliderOutput } from '@uigy/modules/events/models/exclude-from-policy';
import { ResolveSliderOutput } from '@uigy/modules/events/models/interfaces';
import { EventsService } from '@uigy/modules/events/services/events.service';
import { EventsEffects } from '@uigy/modules/events/store/events.effects';
import { ExclusionsDataHelperService } from '@uigy/modules/exclusions/services/exclusions-data-helper.service';
import { FortigateApiService } from '@uigy/modules/settings/modules/servers/fortigate/services/fortigate-api.service';
import { SliderSizeEnumV2 } from '@uigy/shared/components/slider-v2/enums/slider-v2.enum';
import { ToasterType } from '@uigy/shared/components/toaster/enums/toaster-type.enum';
import { ToasterService } from '@uigy/shared/components/toaster/services/toaster.service';
import { WizardPage } from '@uigy/shared/components/wizard/models/wizard-page';
import { WizardService } from '@uigy/shared/components/wizard/services/wizard.service';
import { DatesFormatsEnum } from '@uigy/shared/enums/dateFormats.enum';
import { MenuItem } from '@uigy/shared/models/interfaces';
import { DateTimeService } from '@uigy/shared/services/date-time/date-time.service';
import { IemService } from '@uigy/shared/services/iem.service';
import { LanguageService } from '@uigy/shared/services/language.service';
import { CustomMenuItemParams } from '@uigy/shared/components/ui-grid/models/action-menu';
import { Subject, take } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { PolicyFindingsService } from './policy-findings.service';
import { AppIdsEnum } from '@uigy/shared/directives/app-id/appIds.enum';

@Injectable({
  providedIn: 'root',
})
export class PolicyFindingsActionsService {
  private languageService = inject(LanguageService);
  private wizardService = inject(WizardService);
  private findingsService = inject(PolicyFindingsService);
  private toasterService = inject(ToasterService);
  private exclusionsDataHelperService = inject(ExclusionsDataHelperService);
  private eventsService = inject(EventsService);
  private iemService = inject(IemService);
  private eventsEffects = inject(EventsEffects);
  private fortigateApiService = inject(FortigateApiService);
  private dateTimeService = inject(DateTimeService);

  private findingsTexts = this.languageService.texts.findings;
  private eventsActionsTexts = this.languageService.texts.events.actions;
  private fortigateTexts = this.languageService.texts.settings.servers.fortigate;

  private isFortigateConfigured = false;

  constructor() {
    this.fortigateApiService.getServers().pipe(take(1)).subscribe((servers) => {
      this.isFortigateConfigured = (servers ?? []).length > 0;
    });
  }

  /**
   * Build grid menu items applicable to a single finding (non-bulk selection)
   */
  public gridMenuItemsForFinding(finding: PolicyFindingFragment, refetch$?: Subject<void>): CustomMenuItemParams<PolicyFindingFragment>[] {
    const filter: PolicyFindingsExpressionsParams = {
      field: PolicyFindingFilterField.Id,
      values: [finding.id],
      op: ExprOp.In,
    };

    return [
      {
        label: this.findingsTexts.policy_violations.grid_actions.resolve.button,
        onClick: () => this.openResolveSlider(filter, undefined, false, refetch$),
        capability: Capability.WriteEvents,
        id: AppIdsEnum.findings_action_resolve
      },
      {
        label: this.eventsActionsTexts.exclude.title,
        onClick: () => this.openExcludeSlider(finding, refetch$),
        capability: Capability.WriteEvents,
        id: AppIdsEnum.findings_action_exclude
      },
      {
        label: this.eventsActionsTexts.download_pcap.findings_label,
        onClick: () => this.downloadPcap(finding),
        capability: Capability.PcapDownload,
        disabled: (!finding?.eventType?.canCapture) || this.iemService.isIEM(),
        id: AppIdsEnum.findings_action_pcap_download

      },
      {
        label: this.fortigateTexts.create_policy_btn,
        onClick: () => this.createPolicyOnFortigate(finding.lastHitId || undefined),
        capability: Capability.WritePolicies,
        disabled: !finding?.eventType?.actions?.includes(ActionType.FortiGate) || !this.isFortigateConfigured,
        id: AppIdsEnum.findings_action_create
      },
    ];
  }

  /**
   * Convert grid menu definitions to generic MenuItem list (for drawer).
   */
  public drawerMenuItemsForFinding(finding: PolicyFindingFragment, refetch$?: Subject<void>): MenuItem[] {
    return this.gridMenuItemsForFinding(finding, refetch$).map(gridItem => ({
      label: gridItem.label,
      value: finding.id,
      disabled: gridItem.disabled,
      capability: gridItem.capability,
      action: () => gridItem.onClick([finding]),
    }));
  }

  public openResolveSlider(filter: PolicyFindingsExpressionsParams, search?: string, isBulk = false, refetch$?: Subject<void>, totalCount: number = 0): void {
    const config = {
      header: isBulk ? this.languageService.getTranslationTyped('findings.policy_violations.grid_actions.resolve.bulk_title', { count: totalCount })
        : this.findingsTexts.policy_violations.grid_actions.resolve.title,
      pages: [new WizardPage(ResolveFormComponent)],
      submitBtnText: this.languageService.texts.events.actions.resolve,
      overlay: SliderSizeEnumV2.form,
    };

    this.wizardService.openWizardSlider<ResolveSliderOutput>(config).pipe(
      switchMap((vars) => this.findingsService.resolveFindings({ filter, search, comment: vars.comment })),
      take(1),
    ).subscribe((totalResolved) => {
      if (refetch$) {
        refetch$.next();
      }
      this.toasterService.openToaster({
        message: this.languageService.getTranslationTyped('findings.policy_violations.grid_actions.resolve.success', { count: totalResolved }),
        type: ToasterType.Success,
      });
    });
  }

  private openExcludeSlider(finding: PolicyFindingFragment, refetch$?: Subject<void>): void {
    this.eventsService.getEvent({ id: finding.lastHitId ?? '' }).pipe(take(1)).subscribe((event) => {
      const config = {
        overlay: SliderSizeEnumV2.form,
        header: this.eventsActionsTexts.exclude.title,
        pages: [new WizardPage(ExcludeFromPolicyComponent)],
        submitBtnText: this.languageService.texts.general.buttons.exclude,
        data: {
          isFindings: true,
          type: finding.eventType.exclusion,
          policy: { display: finding.policy?.title ?? null, value: finding.policy.id },
          conditions: { ...this.getExclusionFields(event), policyId: { display: '', value: '' } },
        } as unknown as ExclusionSliderInput,
      };

      this.wizardService.openWizardSlider<ExclusionSliderInput, ExclusionSliderOutput>(config).pipe(
        switchMap((vars) => this.findingsService.excludeFromPolicy(finding.eventType.exclusion, vars)),
        take(1),
      ).subscribe(() => {
        this.toasterService.openToaster({
          message: this.findingsTexts.policy_violations.grid_actions.exclude.toaster.success,
          type: ToasterType.Success,
        });
        if (refetch$) {
          refetch$.next();
        }
      });
    });
  }

  private createPolicyOnFortigate(lastHitId?: string ): void {
    if(lastHitId) {
      this.eventsService.getEvent({ id: lastHitId }).pipe(take(1)).subscribe((event) => {
        this.wizardService.openWizardSlider<EventOrFindingEvent, ReActOnEventResponse>({
          overlay: SliderSizeEnumV2.form,
          pages: [new WizardPage(FortigatePolicyFormComponent)],
          submitBtnText: this.fortigateTexts.create_policy_wizard.submitBtnText,
          header: this.fortigateTexts.create_policy_wizard.header,
          data: { ...event, isFindings: true } as EventOrFindingEvent,
        }).pipe(
          switchMap((data) => this.findingsService.createPolicyOnFortigate(data)),
        ).subscribe();
      });
    }
  }

  private downloadPcap(finding: PolicyFindingFragment): void {
    if (finding.lastHitId) {
      this.eventsEffects.triggerDownloadEventPcap(
        finding.lastHitId,
        `${finding.lastHitId}_${DateTimeService.utcToLocalFormatted(this.dateTimeService.now(), DatesFormatsEnum.exportFileFormat)}.pcap.gz`,
        finding.lastHitId,
      );
    }
  }

  private getExclusionFields(event: GetSingleEventQuery['event']): any {
    switch (event.eventType.exclusion) {
      case ExclusionType.Conversation:
        return this.exclusionsDataHelperService.getConversationExclusionData(event);
      case ExclusionType.Activity:
        return this.exclusionsDataHelperService.getActivityExclusionData(event);
      case ExclusionType.IntrusionDetection:
        return this.exclusionsDataHelperService.getIdsExclusionData(event);
      case ExclusionType.Dnp3:
        return this.exclusionsDataHelperService.getDnpExclusionData(event);
      case ExclusionType.IpConflict:
        return this.exclusionsDataHelperService.getIpConflictExclusionData(event);
      case ExclusionType.TagWrite:
        return this.exclusionsDataHelperService.getTagWriteExclusionData(event);
      case ExclusionType.Asset:
        return this.exclusionsDataHelperService.getAssetExclusionData(event);
      case ExclusionType.Scans:
        return this.exclusionsDataHelperService.getScanExclusionData(event);
      case ExclusionType.OpenPort:
        return this.exclusionsDataHelperService.getPortExclusionData(event);
      case ExclusionType.UsbChange:
        return this.exclusionsDataHelperService.getUsbExclusionData(event);
      case ExclusionType.Iec61850:
        return this.exclusionsDataHelperService.getIec61850ExclusionData(event);
      case ExclusionType.Iec104:
        return this.exclusionsDataHelperService.getIecExclusionData(event);
      case ExclusionType.Goose:
        return this.exclusionsDataHelperService.getGooseExclusionData(event);
      default:
        return this.exclusionsDataHelperService.getSrcDstFields(event);
    }
  }
}
